/**
 * Transport assembly — the one place that decides where events go.
 *
 * When the Dashboards owner asks for a second destination, or the ingest
 * endpoint moves, or someone wants events mirrored somewhere during an
 * incident, this file is the change. Callers, instrumentation, the catalog
 * and the dispatcher are all untouched by it.
 *
 * @module logging/transports
 */

import type { BatchSink, FlushReason, Transport, WireEvent } from '../core/types';
import type { LoggingConfig } from '../config';
import { loggingConfig } from '../config';
import { createBatcher } from '../pipeline/batcher';
import { createConsoleTransport } from './console-transport';
import { createHttpTransport } from './http-transport';

/**
 * Give a batch-oriented sink buffering, byte-aware chunking, and flush
 * scheduling.
 *
 * Written as a decorator rather than folded into the sink so the batching
 * policy is defined once. A second network destination gets identical
 * behaviour by being wrapped, not by copying a queue implementation and
 * slowly diverging from it.
 */
export function withBatching(sink: BatchSink, config: LoggingConfig): Transport {
  const batcher = createBatcher({
    batchSize: config.batchSize,
    flushIntervalMs: config.flushIntervalMs,
    maxQueueSize: config.maxQueueSize,
    maxBodyBytes: config.maxBodyBytes,
    deliver: (batch: readonly WireEvent[], reason: FlushReason) => {
      sink.send(batch, reason);
    },
  });

  return {
    name: `${sink.name}:batched`,
    accept: (event: WireEvent) => {
      batcher.add(event);
    },
    flush: (reason: FlushReason) => {
      batcher.flush(reason);
    },
  };
}

/**
 * Build the active transport set.
 *
 * An empty array is a legitimate outcome — a production build with no
 * ingest endpoint configured emits nothing and makes no requests, which is
 * the correct behaviour for a client that shipped ahead of its backend.
 */
export function createTransports(config: LoggingConfig = loggingConfig): Transport[] {
  const transports: Transport[] = [];

  if (config.consoleEcho) {
    transports.push(createConsoleTransport());
  }

  if (config.endpoint) {
    transports.push(withBatching(createHttpTransport({ endpoint: config.endpoint }), config));
  }

  return transports;
}
